import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type { FeedEntry } from '~/services/layout/index.ts';

/**
 * Manages feed entries lifecycle when circular scrolling is enabled.
 *
 * Responsibilities:
 * - Duplicates feed entries before initial render for seamless circular scrolling
 * - Automatically disables circular scrolling when pagination exists (nextFeedUrl)
 * - Preserves paginated entries from being overwritten
 * - Returns shouldEnableCircular flag for coordinating with other hooks
 *
 * Architecture decision: Pagination and circular scrolling are mutually exclusive.
 * When nextFeedUrl exists, circular scrolling is disabled entirely to prevent
 * complexity around re-duplicating paginated entries.
 *
 * @param baseFeedEntries - Original entries from the feed (non-duplicated)
 * @param circularCondition - Whether circular scrolling should be enabled (from config/autoscroll)
 * @param isLoading - Server/feed loading state
 * @param nextFeedUrl - Pagination URL (circular is disabled if present)
 * @returns Object containing feedEntries, originalFeedEntriesLength, shouldEnableCircular, and setFeedEntries
 */
export function useCircularFeedEntries({
  baseFeedEntries,
  circularCondition,
  isLoading,
  nextFeedUrl,
}: {
  baseFeedEntries: FeedEntry[];
  circularCondition: boolean;
  isLoading: boolean | undefined;
  nextFeedUrl: string | undefined;
}): {
  feedEntries: FeedEntry[];
  originalFeedEntriesLength: number;
  shouldEnableCircular: boolean;
  setFeedEntries: Dispatch<SetStateAction<FeedEntry[]>>;
} {
  // Pagination takes precedence - disable circular entirely when pagination exists
  const shouldEnableCircular = circularCondition && !nextFeedUrl;

  /**
   * Duplicates entries for circular scrolling by creating copies with unique keys.
   * Duplicated entries are appended after originals: [1,2,3,1*,2*,3*]
   */
  const duplicateEntriesForCircular = (entries: FeedEntry[]): FeedEntry[] => {
    const duplicatedEntries = entries.map((entry, i) => {
      const _key = `duplicated-entry-${i}`;
      return {
        ...entry,
        _key,
        id: _key,
      };
    });
    return [...entries, ...duplicatedEntries];
  };

  /**
   * Initialize with duplicated entries if circular should be enabled.
   * This happens before initial render to prevent visual flash.
   */
  const initialFeedEntries = useMemo(() => {
    if (shouldEnableCircular && baseFeedEntries.length > 0) {
      return duplicateEntriesForCircular(baseFeedEntries);
    }
    return baseFeedEntries;
  }, [baseFeedEntries, shouldEnableCircular]);

  const [feedEntries, setFeedEntries] =
    useState<FeedEntry[]>(initialFeedEntries);

  /**
   * Sync with base entries when they change (e.g., preference editor updates).
   * Guard: Preserve entries if pagination has appended more (regardless of circular state).
   */
  useEffect(() => {
    if (isLoading) return;

    // Don't reset if pagination has appended entries
    // This guard works regardless of circular state
    if (feedEntries.length > initialFeedEntries.length) {
      return;
    }

    setFeedEntries(initialFeedEntries);
  }, [initialFeedEntries, feedEntries.length, isLoading]);

  return {
    feedEntries,
    originalFeedEntriesLength: baseFeedEntries.length,
    shouldEnableCircular,
    setFeedEntries,
  };
}
